import { ComponentType } from '@angular/cdk/overlay';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from './custom-snackbar/custom-snackbar.component';

const DEFAULT_SNACKBAR_CONFIG: MatSnackBarConfig = {
  duration: 6000,
  verticalPosition: 'bottom',
  horizontalPosition: 'center',
};

type CustomConfig = MatSnackBarConfig<CustomSnackbarComponent['data']> & { queueDuration: number };

@Injectable({ providedIn: 'root' })
export class SnackbarService {
  private matSnackBar = inject(MatSnackBar);
  private active?: {
    time: number;
    ref: MatSnackBarRef<CustomSnackbarComponent>;
    config: CustomConfig;
  };
  private queue: CustomConfig[] = [];

  openSnackBar<T>(messageOrComponent: string | ComponentType<T>, config?: MatSnackBarConfig) {
    config = { ...DEFAULT_SNACKBAR_CONFIG, ...config };
    return typeof messageOrComponent === 'string'
      ? this.matSnackBar.open(messageOrComponent, undefined, config)
      : this.matSnackBar.openFromComponent(messageOrComponent, config);
  }

  error(message: string) {
    this.openCustom({
      queueDuration: 6_500,
      duration: 10_000,
      data: { icon: 'cancel', message, color: '#f44336' },
    });
  }
  success(message: string) {
    this.openCustom({
      queueDuration: 3_500,
      duration: 5_000,
      data: { icon: 'check_circle', message, color: '#4caf50' },
    });
  }
  info(message: string) {
    this.openCustom({
      queueDuration: 4_000,
      duration: 6_000,
      data: { icon: 'info', message, color: '#2196f3' },
    });
  }
  private openCustom(config: CustomConfig) {
    if (this.active && this.queue.length < 4) return this.openQueued(config);
    else if (this.active) return;
    const ref = this.openSnackBar(CustomSnackbarComponent, config) as any;
    this.active = { time: Date.now(), ref, config };
    ref.afterDismissed().subscribe(() => {
      this.active = undefined;
      if (this.queue.length) this.openCustom(this.queue.shift());
    });
  }
  private openQueued(_config: CustomConfig) {
    _config.duration = _config.queueDuration;
    this.queue.push(_config);
    const { time, ref, config } = this.active;
    const remaining = config.queueDuration - (Date.now() - time);
    setTimeout(() => ref.dismiss(), remaining);
  }

  openFromComponent(
    component: ComponentType<unknown>,
    config?: MatSnackBarConfig<any>,
    onDismissWithActionCallback?: () => void,
    onDismissCallback?: () => void,
  ) {
    const snackBarRef = this.matSnackBar.openFromComponent(component, config);
    if (onDismissWithActionCallback) {
      snackBarRef.onAction().subscribe(() => onDismissWithActionCallback());
    }
    if (onDismissCallback) {
      snackBarRef.afterDismissed().subscribe(() => onDismissCallback());
    }
  }
}
